import React, { useEffect, useState } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { TreeView } from '@mui/x-tree-view/TreeView';
import { TreeItem } from '@mui/x-tree-view/TreeItem';
import FolderIcon from '@mui/icons-material/Folder';
import DescriptionIcon from '@mui/icons-material/Description';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import Grid from '@mui/material/Grid';

import ErrorAlert from './error-alert';
import { Button, Checkbox, FormControlLabel, IconButton, TextField } from '@mui/material';
import { LoadingDialog } from '@kepler.gl/components';
import { findAndAddFolder, findAndAddToObject, findAndRemoveObject } from './file-utils';
import { uploadFiles } from '../../api/files';
import { set } from '@kepler.gl/utils';
import { UPLOAD_STATUS } from './constants';

const FileExplorer = ({
  filesList,
  files,
  selectFiles,
  showFileExplorer,
  uploadStatus,
  setUploadStatus
}) => {

  const [checked, setChecked] = useState({});
  const [error, setError] = useState(false);
  const [fileSelected, setFileSelected] = useState();
  const [currentList, setCurrentList] = useState({});
  const [tempFolderName, setTempFolderName] = useState("");

  const [isLoading, setIsLoading] = useState(false);

  //For adding folders
  const [addFolderAttributes, setAddFolderAttributes] = useState({});

  //Default expansion
  const [expanded, setExpanded] = useState(['']);
  const FILE_SELECTION_LIMIT = 3;

  useEffect(() => {
    if(Object.keys(filesList).length === 0){
      setIsLoading(true);
    }else{
      setCurrentList(filesList)
      setIsLoading(false);
    }
  }, [filesList])

  useEffect(() => {
    const prevSelectedFiles = {}
    files.forEach((file) => {
      prevSelectedFiles[file] = true
    })
    setChecked(prevSelectedFiles);
  }, [files])

  useEffect(() => {
    if (uploadStatus === UPLOAD_STATUS.UPLOADING) {
      const fileNameWithPath = fileSelected.path + fileSelected.input.name;
      uploadFiles(fileNameWithPath,fileSelected).then(() => {
        setUploadStatus(UPLOAD_STATUS.FINISHED);
      })
      .catch(error => {
        console.error(error);
        setUploadStatus(UPLOAD_STATUS.INITIAL_LOAD);
      });;
    }
  }, [uploadStatus]);

  useEffect(() => {
    if (Object.keys(addFolderAttributes).length !== 0) {
      const folderName = addFolderAttributes?.name
      const folderPath = addFolderAttributes?.path

      const addFolder = async () => {
        let newList = window.structuredClone(currentList);
        newList = findAndAddToObject(newList, folderName, folderPath);
        return newList;
      }
      addFolder().then((res) => {
        setCurrentList(res);
        setExpanded((prev) => {
          return [...prev, folderPath, `${folderPath}/ADD_FOLDER`]
        })
      });
    }
  }, [addFolderAttributes])
  
  const FileLabel = ({ file }) => (
    <Grid container direction="row" alignItems="center" wrap="nowrap">
      <Grid item>
        <DescriptionIcon />
      </Grid>
      <Grid item>
        {file.name}
      </Grid>
    </Grid>
  )

  const FileLabelContainer = ({ file }) => {
    const isFileSelectLimitReached = (fileName) => {
      if (Object.keys(checked).length === FILE_SELECTION_LIMIT && !checked.hasOwnProperty(fileName)) {
        setError(true)
      } else {
        setError(false);
      }
    }

    const handleChange = async (isChecked, fileName) => {
      if (checked.hasOwnProperty(fileName) && !isChecked) {
        setChecked(prevState => {
          const state = { ...prevState }
          delete state[fileName]
          return state;
        });
        setError(false);
      } else {
        if (Object.keys(checked).length < FILE_SELECTION_LIMIT) {
          setChecked({ ...checked, [fileName]: isChecked });
        }
      }
    }

    return (
      <Grid container direction="row" alignItems="center" wrap="nowrap">
        <Grid item>
          <FormControlLabel onClick={(e) => isFileSelectLimitReached(file.path)}
            disabled={(Object.keys(checked).length === 3) && !checked.hasOwnProperty(file.path)}
            key={file.name} control={<Checkbox />}
            onChange={(e, isChecked) => handleChange(isChecked, file.path)}
            value={file.path}
            label={<FileLabel file={file} />}
            checked={checked.hasOwnProperty(file.path) && checked[file.path]}
          />
        </Grid>
      </Grid>
    )
  }

  const FileItem = ({ file }) => {
    return <TreeItem nodeId={file.path} label={<FileLabelContainer file={file} />} style={{ marginLeft: 15, paddingLeft: 18 }} />;
  };

  const AddFolderItem = ({ folderName, folderPath }) => {
    const handleRemoveFolder = (folderName, parentFolderName, folderPath) => {
      if (!folderName) {
        const removeFolder = async () => {
          let newList = window.structuredClone(currentList);
          newList = findAndRemoveObject(newList, parentFolderName, folderPath);
          return newList;
        }
        removeFolder().then((res) => {
          setCurrentList(res);
        })
      } else {
        if (folderName.length > 0) {
          const addNewFolder = async () => {
            let newList = window.structuredClone(currentList);
            newList = findAndAddFolder(newList, folderName, folderPath);
            return newList;
          }
          addNewFolder().then((res) => {
            setCurrentList(res);
            //Reset folder name
            setTempFolderName("")
          })
        }
      }
  
    }

    return (
      <Grid item>
        <TextField
          hiddenLabel
          id="filled-hidden-label-small"
          defaultValue=""
          value={tempFolderName}
          variant="filled"
          size="small"
          placeholder="New folder"
          autoFocus
          onBlur={(e) => handleRemoveFolder(e.target.value, folderName, folderPath)}
          onChange={(e) => setTempFolderName(e.target.value)}
        />
      </Grid>
    )
  }

  const FolderLabelContainer = ({ folder }) => {
    const [hovered, setHovered] = useState({});
    const [uploadClicked, setUploadClicked] = useState(false);

    const onMouseHover = (folderPath) => {
      if (!folderPath?.includes('ADD_FOLDER')) {
        setHovered((prevState) => {
          const state = { [folderPath]: true }
          return state;
        })
      }
    }

    const onMouseLeave = (folderPath) => {
      if (!folderPath?.includes('ADD_FOLDER') && !uploadClicked) {
        setHovered((prevState) => {
          const state = { [folderPath]: false }
          return state;
        })
      }
    }

    const onAddFolderClicked = (e, name, path) => {
      e.stopPropagation();
      const addFolderAttributes = {
        name,
        path
      }
      setAddFolderAttributes(addFolderAttributes);
    }
  
    const onFileSelected = (e, path) => {
      e.preventDefault();
      const pathOnServer = path === "" ? "" : path + "/";
      const file = { input: e.target.files[0], path: pathOnServer };

      setUploadClicked(false);
      window.removeEventListener('focus', handleNativeFileExplorer);
      setFileSelected(file);
      setUploadStatus(UPLOAD_STATUS.UPLOADING);
    }

    const handleInputClicked = (e) => {
      e.stopPropagation()
      setUploadClicked(true);
      window.addEventListener('focus', handleNativeFileExplorer);
    }
    const handleNativeFileExplorer = (e) => {
      setUploadClicked(false);
      window.removeEventListener('focus', handleNativeFileExplorer);
    }

    return (
      <div onMouseEnter={(e) => onMouseHover(folder.path)} onMouseLeave={(e) => onMouseLeave(folder.path)}>
        <Grid container direction="row" alignItems="center" wrap="nowrap">
          <Grid item>
            <FolderIcon />
          </Grid>
          {folder.name.includes('ADD_FOLDER') ?
            <AddFolderItem folderName={folder.name} folderPath={folder.path} />
            :
            <Grid item>
              {folder.path === "" ? folder.name.replace("root", "") : folder.name}
            </Grid>
          }
          {hovered[folder.path] || uploadClicked ?
            <Grid item container display="flex" justifyContent={'flex-end'} >
              <input accept=".json,.geojson,.csv" id={`${folder.path}-input`} type="file" hidden onClick={handleInputClicked}
               onChange={(e, input) => onFileSelected(e, folder.path)} 
               />
              <label htmlFor={`${folder.path}-input`}>
                <IconButton color="primary" component="span" onClick={(e) => e.stopPropagation()}>
                  <NoteAddIcon />
                </IconButton>
              </label>
              <IconButton onClick={(e) => onAddFolderClicked(e, folder.name, folder.path)}>
                <CreateNewFolderIcon />
              </IconButton>
            </Grid>
            : null
          }
        </Grid>
      </div>
    )
  }

  const FolderItem = ({ folder }) => {
    return (
      <TreeItem nodeId={folder.path} label={<FolderLabelContainer folder={folder} />}
        style={{ marginLeft: 15, paddingLeft: 18 }}
        onNodeSelect={(e, nodeId) => { handleSelect(e, nodeId) }}
      >
        {Object.values(folder.contents).map(item => (
          <Item key={item.name} item={item} />
        ))}
      </TreeItem>
    );
  };

  const Item = ({ item }) => {
    if (item.type === 'file' && item.category === 'data') {
      return <FileItem file={item} />;
    } else if (item.type === 'directory') {
      return <FolderItem folder={item} />;
    }
    return ""
  };

  const handleSelect = (e, id) => {
    const index = expanded.indexOf(id);
    const copyExpanded = [...expanded];
    if (index === -1) {
      copyExpanded.push(id);
    } else {
      copyExpanded.splice(index, 1);
    }
    setExpanded(copyExpanded);
  }

  const handleSubmit = () => {
    showFileExplorer(false);
    const selected = []
    for (const [key, value] of Object.entries(checked)) {
      selected.push(key);
    }
    selectFiles(selected)
  }
  // Assuming the root item is a folder
  //   return <FolderItem folder={response} />;
  return (
    isLoading ? 
    <LoadingDialog size={64} message={"Retrieving files from server..."}/> 
    :
      <>
        <TreeView
          defaultCollapseIcon={<ExpandMoreIcon />}
          defaultExpandIcon={<ChevronRightIcon />}
          expanded={expanded}
          onNodeSelect={(e, nodeId) => { handleSelect(e, nodeId) }}
        >
          <Item item={currentList} />
        </TreeView>
        {uploadStatus === UPLOAD_STATUS.UPLOADING ? <LoadingDialog size={64} message={"Uploading your file..."}/> : null}
        <Button variant="contained" onClick={(e) => handleSubmit()}>Select files</Button>
        {error && <ErrorAlert message={"Can't select more than three files. Please deselect one and try again"}></ErrorAlert>}
      </>
      
  )
};

export default FileExplorer;